import moment from 'moment-timezone';
import { renderRoundLabel } from './draws';
import { drawCategoryTypeIds, drawEliminationTypeIds, drawPositionTypeIds, sportTypeIds } from './eventConstants';
import getOptionsForTimeSelect from '../components/Form/utils';
import { OutComeTypes } from './postScoreConstants';
import { isPlayerCheckedIntoDraw } from './events';
import { titleCase, TIME_FORMAT_12H } from './formatting';
import { OUTCOMES_TO_ALLOW_SCORING, OUTCOMES_TO_CONSIDER_AS_NOT_COMPLETED, OUTCOMES_WITH_NO_WINNER, OUTCOMES_WITH_WINNER_BY_SCORE, OUTCOMES_WITH_WINNER_BY_TEAM_OR_PLAYER_SELECTOR } from '../pages/DrawEditPage/EventDeskPostScore/consts';
import { pos1Won, pos2Won } from './postScore';

export const TBD_DISPLAY = 'TBD';

export const hasChildDraws = (draw) => {
    return draw.childDraws?.length > 0;
};

export const shouldDisplayMainDrawAsChildDraw = (draw) => {
    if (draw.eliminationTypeId === drawEliminationTypeIds.ROUND_ROBIN) {
        return draw.categoryTypeId !== drawCategoryTypeIds.GROUPED && draw.childDraws?.length > 0;
    }

    if (draw.eliminationTypeId === drawEliminationTypeIds.AD_HOC) {
        return draw.childDraws?.length > 0;
    }

    if (draw.categoryTypeId === drawCategoryTypeIds.PRO_DRAW) {
        return true;
    }

    const mainDrawEliminationTypes = [
        drawEliminationTypeIds.SINGLE_ELIMINATION,
        drawEliminationTypeIds.FIRST_MATCH_BACKDRAW,
        drawEliminationTypeIds.DOUBLE_ELIMINATION,
        drawEliminationTypeIds.FEED_IN_CONSOLATION,
        drawEliminationTypeIds.COMPASS,
    ];
    return mainDrawEliminationTypes.includes(draw.eliminationTypeId);
};

let colorIndex = 1;

export const resetColorIndex = () => {
    colorIndex = 1;
};

export const generateRandomColor = () => {
    const colorsArray = [
        '#832da4',
        '#c3291c',
        '#4599df',
        '#fb7800',
        '#475565',
        '#16ba1c',
        '#4350af',
    ];

    const colorIndexToUse = colorIndex > colorsArray.length ? (colorIndex % colorsArray.length) : colorIndex - 1;
    colorIndex += 1;
    return colorsArray[colorIndexToUse];
};

export const addOpacityToHexColor = (colorHex, opacity) => {
    // coerce values so it is between 0 and 1.
    const _opacity = Math.round(Math.min(Math.max(opacity ?? 1, 0), 1) * 255);
    const color = colorHex.substring(1);
    return `#${color}${_opacity.toString(16).toUpperCase()}`;
};

export const getAllottedMatchTime = (match, sportTypeId = sportTypeIds.TENNIS, parentDraw = null) => {
    const matchTime = match?.allottedMatchTime || parentDraw?.allottedMatchTime;

    return matchTime ||
        (sportTypeId === sportTypeIds.TENNIS
            ? DEFAULT_ALLOTTED_TIME_FOR_TENNIS
            : DEFAULT_ALLOTTED_TIME_FOR_PICKLEBALL);
};

export const getMatchScoringType = (match, parentDraw) => {
    return match?.scoringTypeId || parentDraw?.scoringTypeId;
};

export const getDrawName = (childDraw, parentDraw, drawCategoryTypesDict, isForV1 = false) => {
    if (isForV1) {
        if (parentDraw?.drawCategoryType?.id === drawCategoryTypeIds.PRO_DRAW) {
            if (childDraw?.drawCategoryType?.id === drawCategoryTypeIds.PRO_DRAW) {
                return drawCategoryTypesDict[childDraw.drawCategoryType.id]?.label;
            }

            return childDraw?.name;
        }

        if (parentDraw?.drawCategoryType?.id === drawCategoryTypeIds.GROUPED) {
            return childDraw?.name;
        }

        return drawCategoryTypesDict[childDraw?.drawCategoryType.id]?.label;
    }

    if (parentDraw?.categoryTypeId === drawCategoryTypeIds.PRO_DRAW) {
        if (childDraw?.categoryTypeId === drawCategoryTypeIds.PRO_DRAW) {
            return drawCategoryTypesDict[childDraw.categoryTypeId]?.label;
        }

        return childDraw?.name;
    }

    if (parentDraw?.categoryTypeId === drawCategoryTypeIds.GROUPED) {
        return childDraw?.name;
    }

    return drawCategoryTypesDict[childDraw?.categoryTypeId]?.label;
};

export const getParentDrawName = (childDraw, parentDraw) => {
    if (!parentDraw) {
        return childDraw?.name;
    }

    return parentDraw?.name;
};

export const getUnScheduledMatchFromDrawMatch = (
    match,
    sportTypeId,
    drawCategoryTypesDict,
    drawsList = [],
) => {
    const { draw, parentDraw, color } = getDrawData(drawsList, match.drawId);

    return {
        ...match,
        drawCategoryTypeId: draw?.categoryTypeId,
        drawEliminationTypeId: draw?.eliminationTypeId,
        drawFormatTypeId: draw?.formatTypeId,
        drawBallTypeId: draw?.ballTypeId,
        drawIsTimeBased: draw?.isTimeBased,
        drawName: getDrawName(draw, parentDraw, drawCategoryTypesDict),
        parentDrawId: parentDraw?.id,
        drawColor: color,
        scoringTypeId: match.scoringTypeId || parentDraw?.scoringTypeId,
        allottedMatchTime: getAllottedMatchTime(match, sportTypeId, parentDraw),
    };
};

export const getSinglesPlayerDisplayNameOfMatch = (position, participatingMembersOrDict = []) => {
    if (Array.isArray(participatingMembersOrDict)) {
        const memberId = position.memberIds?.[0] ?? position.memberId;
        const selectedPlayer = participatingMembersOrDict.find((m) => m.id === memberId);
        if (!selectedPlayer) {
            return TBD_DISPLAY;
        }
        return `${selectedPlayer.firstName} ${selectedPlayer.lastName}`;
    }

    const selectedPlayer = participatingMembersOrDict[position.memberId];
    return `${selectedPlayer?.firstName} ${selectedPlayer?.lastName}`;
};

export const getDoublesPlayerDisplayNameOfMatch = (position, participatingMembersOrDict = [], playerIndexOrTeamsDict = 0, playerIndex = 0) => {
    if (Array.isArray(participatingMembersOrDict)) {
        const memberIds = position.memberIds ?? [];
        const memberId = memberIds[playerIndexOrTeamsDict];
        const selectedPlayer = participatingMembersOrDict.find((m) => m.id === memberId);
        if (!selectedPlayer) {
            return TBD_DISPLAY;
        }
        const firstNameInitial = selectedPlayer.firstName?.[0] || '';
        return `${firstNameInitial}. ${selectedPlayer.lastName}`;
    }

    const teamsDict = playerIndexOrTeamsDict;
    const team = teamsDict[position.teamId];
    const memberId = team?.members[playerIndex];
    const selectedPlayer = participatingMembersOrDict[memberId];
    const firstNameInitial = selectedPlayer?.firstName[0];
    return `${firstNameInitial}. ${selectedPlayer?.lastName}`;
};

export const shouldDisplayCheckedInMark = (position, match, participatingMembersOrDict = [], playerIndexOrTeamsDict = 0, playerIndex = 0) => {
    if (Array.isArray(participatingMembersOrDict)) {
        const memberIds = position?.memberIds ?? [];
        const memberId = position?.typeId === drawPositionTypeIds.PLAYER
            ? (memberIds[0] ?? position?.memberId)
            : memberIds[playerIndexOrTeamsDict];

        if (!memberId) {
            return false;
        }

        const player = participatingMembersOrDict.find((m) => m.id === memberId);
        return isPlayerCheckedIntoDraw(player, match.drawId);
    }

    const playersDictByMemberId = participatingMembersOrDict;
    const teamsDict = playerIndexOrTeamsDict;
    if (position?.typeId === drawPositionTypeIds.PLAYER) {
        const memberId = position?.memberId;
        return isPlayerCheckedIntoDraw(playersDictByMemberId[memberId], match.drawId);
    }

    if (position?.typeId === drawPositionTypeIds.TEAM) {
        const team = teamsDict[position.teamId];
        const memberId = team?.members[playerIndex];
        return isPlayerCheckedIntoDraw(playersDictByMemberId[memberId], match.drawId);
    }

    return false;
};

export const getPositionDisplayNameOfMatch = (positionParam, match, playersDictByMemberId = {}, teamsDict = {}) => {
    const position = match[positionParam];
    if (!position) {
        return 'TBD';
    }

    if (position.typeId === drawPositionTypeIds.BYE) {
        return 'Bye';
    }

    // V3 path: match has participatingMembers with memberIds on positions
    if (match.participatingMembers) {
        const { participatingMembers } = match;
        const memberIds = position.memberIds ?? (position.memberId ? [position.memberId] : []);

        if (memberIds.length === 0) {
            return 'TBD';
        }

        const names = memberIds.map((memberId) => {
            const player = participatingMembers.find((m) => m.id === memberId);
            if (player) {
                return `${player.firstName} ${player.lastName}`;
            }
            return 'TBD';
        });

        return names.join(' / ');
    }

    // Legacy path: uses playersDictByMemberId and teamsDict
    if (position.typeId === drawPositionTypeIds.PLAYER) {
        const selectedPlayer = playersDictByMemberId[position.memberId];
        return `${selectedPlayer?.firstName} ${selectedPlayer?.lastName}`;
    }
    if (position.typeId === drawPositionTypeIds.TEAM) {
        const selectedTeam = teamsDict[position.teamId];
        return selectedTeam.displayName;
    }
    return 'TBD';
};

export const getMatchDroppedDisplayString = (match, playersDictByMemberId, teamsDict) => {
    const hasPosition1WonGame = hasPositionWon('position1', match, playersDictByMemberId, teamsDict);
    let loosingPlayerOrTeamName;
    if (hasPosition1WonGame) {
        loosingPlayerOrTeamName = getPositionDisplayNameOfMatch('position2', match, playersDictByMemberId, teamsDict);
    } else {
        loosingPlayerOrTeamName = getPositionDisplayNameOfMatch('position1', match, playersDictByMemberId, teamsDict);
    }

    let droppedType;
    if (match.result?.outcome === OutComeTypes.DEFAULTED) {
        droppedType = 'Default';
    } else if (match.result?.outcome === OutComeTypes.WITHDREW) {
        droppedType = 'Withdrew';
    } else if (match.result?.outcome === OutComeTypes.RETIRED) {
        droppedType = 'Retired';
    } else if (match.result?.outcome === OutComeTypes.FORFEIT) {
        droppedType = 'Forfeited';
    } else {
        droppedType = '';
    }

    return `${droppedType}: ${loosingPlayerOrTeamName}`;
};

export const getRoundName = (match) => {
    const round = {
        number: match.roundNumber,
        id: match.roundId,
        matches: Array.from({ length: match.roundMatchCount }),
    };
    let nextRound;
    if (match.nextRoundMatchCount) {
        nextRound = {
            matches: Array.from({ length: match.nextRoundMatchCount }),
        };
    }

    return renderRoundLabel(round, match.drawEliminationTypeId, match.drawCategoryTypeId, nextRound);
};

const timeIncrements = [5, 10, 15, 30];

export const allowedTimePeriodIncrements = timeIncrements.map((time) => ({
    id: time,
    value: time,
    label: `${time} mins`,
}));

export const snapDownToNearestIncrement = (value) => {
    if (timeIncrements.includes(value)) {
        return value;
    }

    const valid = timeIncrements.filter(v => v <= value);
    return valid.length ? valid[valid.length - 1] : timeIncrements[0];
};

export const calculateSmartTimeIncrement = (drawsList, sportTypeId) => {
    const sportFallback = sportTypeId === sportTypeIds.PICKLEBALL ? 5 : 15;

    if (!drawsList?.length) {
        return sportFallback;
    }

    const allDraws = drawsList.flatMap(d => [d, ...(d.childDraws || [])]);
    const times = allDraws
        .map(d => d.allottedMatchTime)
        .filter(t => typeof t === 'number' && t > 0);

    if (!times.length) {
        return sportFallback;
    }

    const minAllottedTime = times.reduce((a, b) => Math.min(a, b));
    const target = Math.round(minAllottedTime / 3);
    return snapDownToNearestIncrement(target);
};

const pushBackTimeIncrements = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100, 105, 110, 115, 120];

export const allowedPushBackTimePeriodIncrements = pushBackTimeIncrements.map((time) => ({
    id: time,
    value: time,
    label: `${time} mins`,
}));

export const dateTimeFormat = 'MMM DD, hh:mm A';
export const dateFormat = 'MMM DD, YYYY';
export const courtGridDateFormat = 'ddd, MMM DD';
export const courtGridPublishConfirmDateFormat = 'ddd, MMM DD, YYYY';
export const dateFormatForDayConfig = 'YYYY-MM-DDTHH:mm:ss';
export const timeFormatForDayConfig = 'HH:mm';
export const defaultStartTime = '08:30';
export const defaultEndTime = '23:59';
export const defaultTimeIncrements = 5;
export const allCourtsCheckboxKey = 'all-courts';
export const DEFAULT_ALLOTTED_TIME_FOR_PICKLEBALL = 30;
export const DEFAULT_ALLOTTED_TIME_FOR_TENNIS = 60;
export const DAY_END_GRACE_PERIOD_MINUTES = 1;
export const MIDNIGHT_TIME = '00:00';
export const END_OF_DAY_LABEL = 'End of day';

export const CONFLICT_MESSAGE_BEFORE_DAY_START = 'Match cannot be scheduled before day start time';
export const CONFLICT_MESSAGE_AFTER_DAY_END = 'Match cannot be scheduled after day end time';
export const CONFLICT_MESSAGE_BEFORE_DRAW_START = 'Match cannot be scheduled before draw start time';
export const CONFLICT_MESSAGE_OTHER_MATCH_ON_COURT = 'Match cannot be scheduled as another match is already scheduled on this court at this time';
export const CONFLICT_MESSAGE_BEFORE_STARTED_MATCH = 'Match cannot be scheduled before match that has already been started';
export const CONFLICT_TITLE_SCHEDULE = 'Match cannot be scheduled';
export const CONFLICT_TITLE_RESCHEDULE = 'Match cannot be moved';
export const matchSchedulerStatuses = {
    IN_PROGRESS: 1,
    PAUSED: 2,
    COMPLETED: 3,
};

export const getDateObject = (currentDateUtc, eventStartUtc, eventEndDate, timeZone) => {
    const currentDayStartTime = moment.utc(currentDateUtc).tz(timeZone.value).startOf('day');
    const currentDayEndTime = moment(currentDateUtc).endOf('day');

    let dayStartTimeUtc = moment(currentDayStartTime);
    if (dayStartTimeUtc.isBefore(moment(eventStartUtc))) {
        dayStartTimeUtc = moment(eventStartUtc);
    }

    let dayEndTimeUtc = moment(currentDayEndTime);
    if (dayEndTimeUtc.isAfter(moment(eventEndDate))) {
        dayEndTimeUtc = moment(eventEndDate);
    }

    return {
        dayStartTimeUtc,
        dayStartTimeUtcString: dayStartTimeUtc.toString(),
        dayEndTimeUtc,
        dayEndTimeUtcString: dayEndTimeUtc.toString(),
    };
};

export const getOptionsForCourtSchedulerTimeSelect = (timeZone, { includeEndOfDay = false } = {}) => {
    const isoAbbr = moment().tz(timeZone).format('z');
    const timeSelectOptions = getOptionsForTimeSelect();

    const timeZoneAbbr = isoAbbr.startsWith('+') ? '' : ` ${isoAbbr}`;

    const baseOptions = timeSelectOptions.map((tsOption) => ({
        ...tsOption,
        title: `${tsOption.title}${timeZoneAbbr}`,
        id: tsOption.value,
    }));

    if (includeEndOfDay) {
        return baseOptions
            .filter(opt => opt.value !== MIDNIGHT_TIME)
            .concat({ value: defaultEndTime, title: END_OF_DAY_LABEL });
    }
    return baseOptions;
};

export const getDateTimeObjectFromCurrentDateAndTime = (currentDate, time, eventSchedule) => {
    const currentDateObj = moment.utc(currentDate).tz(eventSchedule?.timeZone?.value);
    const timeValue = time.split(':');
    currentDateObj.add(timeValue[0], 'hours');
    currentDateObj.add(timeValue[1], 'minutes');

    return currentDateObj.toISOString();
};

export const getDefaultFacilitiesSelected = (activeFacilitiesAndCourts, config) => {
    const facilities = {};

    activeFacilitiesAndCourts.forEach((facility) => {
        const courts = {};
        let isAnyCourtNotSelected = false;
        let isAtLeastOneCourtSelected = false;

        facility.courts.forEach((court) => {
            const isCourtSelected = config?.courts?.find((configCourt) => configCourt.courtId === court.id);
            courts[court.id] = { selected: isCourtSelected };

            isAnyCourtNotSelected = isAnyCourtNotSelected || !isCourtSelected;
            isAtLeastOneCourtSelected = isAtLeastOneCourtSelected || isCourtSelected;
        });

        if (facility.courts.length > 1) {
            courts[allCourtsCheckboxKey] = { selected: !isAnyCourtNotSelected };
        }

        facilities[facility.id] = {
            selected: isAtLeastOneCourtSelected,
            courts,
        };
    });

    if (activeFacilitiesAndCourts.length === 1) {
        const facilityId = activeFacilitiesAndCourts[0].id;
        facilities[facilityId].selected = true;
    }

    return facilities;
};

export const getDefaultValuesFromConfigObject = (config, eventSchedule, activeFacilitiesAndCourts, selectedDate = null) => {
    const { timeZone } = eventSchedule;

    const startTime = moment.utc(config.startTime).tz(timeZone?.value).format(timeFormatForDayConfig);
    const endTime = moment.utc(config.endTime).tz(timeZone?.value).format(timeFormatForDayConfig);

    const dateToUse = selectedDate || config.date;

    return {
        date: moment.utc(dateToUse).tz(timeZone?.value).toISOString(),
        startTime,
        endTime,
        timeIncrements: snapDownToNearestIncrement(config.timeIncrements),
        followsByMatchStartType: config.followsByMatchStartType,
        facilities: getDefaultFacilitiesSelected(activeFacilitiesAndCourts, config),
    };
};

export const getSelectedCourtIds = (facilities) => {
    const courtIds = [];
    const facilityKeys = Object.keys(facilities);
    facilityKeys.forEach((fKey) => {
        if (facilities[fKey].selected) {
            const courtKeys = Object.keys(facilities[fKey].courts).filter(key => key !== allCourtsCheckboxKey);
            courtKeys.forEach((cKey) => {
                if (facilities[fKey].courts[cKey].selected) {
                    courtIds.push(cKey);
                }
            });
        }
    });

    return courtIds;
};

export const validateStartTime = (formApi, selectedDate, eventSchedule) => (value) => {
    const eventStartDate = moment.utc(eventSchedule.eventStartUtc).tz(eventSchedule.timeZone?.value);

    const currentDayStartTimeString = getDateTimeObjectFromCurrentDateAndTime(selectedDate, value, eventSchedule);
    const endTimeValue = formApi.getValue('endTime');
    const currentDayEndTimeString = getDateTimeObjectFromCurrentDateAndTime(selectedDate, endTimeValue, eventSchedule);

    const currentDayStartTime = moment.utc(currentDayStartTimeString).tz(eventSchedule.timeZone?.value);
    const currentDayEndTime = moment.utc(currentDayEndTimeString).tz(eventSchedule.timeZone?.value);

    if (!currentDayEndTime.isAfter(currentDayStartTime)) {
        return 'Start time should be before end time';
    }

    if (currentDayStartTime.isBefore(eventStartDate)) {
        return 'Start time should not be before event start time';
    }
};

export const validateEndTime = (formApi, selectedDate, eventSchedule) => (value) => {
    const startTimeValue = formApi.getValue('startTime');
    const eventEnd = moment.utc(eventSchedule.eventEndUtc).tz(eventSchedule.timeZone?.value);

    if (value === defaultEndTime) {
        if (startTimeValue === defaultEndTime) {
            return 'Start time should be before end time';
        }
        // Compare end-of-day boundaries so 23:59 on the event's last calendar day is accepted
        // regardless of the event's specific end time-of-day.
        const eventLastDay = eventEnd.clone().endOf('day');
        if (moment(selectedDate).endOf('day').isAfter(eventLastDay)) {
            return 'End time should not be after event end time';
        }
        return undefined;
    }

    const currentDayStartTimeString = getDateTimeObjectFromCurrentDateAndTime(selectedDate, startTimeValue, eventSchedule);
    const currentDayEndTimeString = getDateTimeObjectFromCurrentDateAndTime(selectedDate, value, eventSchedule);

    const currentDayStartTime = moment.utc(currentDayStartTimeString).tz(eventSchedule.timeZone?.value);
    const currentDayEndTime = moment.utc(currentDayEndTimeString).tz(eventSchedule.timeZone?.value);

    if (!currentDayEndTime.isAfter(currentDayStartTime)) {
        return 'Start time should be before end time';
    }

    if (currentDayEndTime.isAfter(eventEnd)) {
        return 'End time should not be after event end time';
    }
};

export const hasInValidCourtSelection = (formValues) => {
    const courtIds = getSelectedCourtIds(formValues.facilities);

    return courtIds.length === 0;
};

export const getActiveFacilitiesAndCourts = (locations = []) => {
    return locations.reduce((list, location) => {
        if (location.enabled) {
            const updatedFacilitiesList = [];
            if (location.facilities?.length > 0) {
                location.facilities.forEach((facility) => {
                    if (facility.enabled) {
                        const updatedCourts = facility.courts
                            .filter(court => court.enabled)
                            .sort((court1, court2) => court1.name.localeCompare(court2.name));
                        updatedFacilitiesList.push({
                            ...facility,
                            courts: updatedCourts,
                        });
                    }
                });
            }

            return list.concat(updatedFacilitiesList);
        }
        return list;
    }, []);
};

export const getFacilitiesAndCourtsToDisplay = (facilitiesAndCourts = [], courtConfig = null) => {
    const facilitiesAndCourtsToDisplay = [];

    facilitiesAndCourts.forEach((facility) => {
        if (courtConfig) {
            const displayableCourts = facility.courts?.filter((court) => {
                return courtConfig.courts.find((configCourt) => configCourt.courtId === court.id);
            }) || [];
            if (displayableCourts.length > 0) {
                facilitiesAndCourtsToDisplay.push({
                    ...facility,
                    courts: displayableCourts,
                });
            }
        } else {
            facilitiesAndCourtsToDisplay.push({
                ...facility,
            });
        }
    });

    return facilitiesAndCourtsToDisplay;
};

export const createScheduledMatchObjectFromUnscheduledMatch = (
    match,
    start,
    courtId,
    facilityId,
    eventSchedule,
) => {
    const { allottedMatchTime } = match;

    return {
        ...match,
        courtId,
        startTime: moment.utc(start).tz(eventSchedule?.timeZone?.value).toISOString(),
        endTime: moment.utc(start).tz(eventSchedule?.timeZone?.value).add(allottedMatchTime, 'minutes').toISOString(),
        facilityId,
        isPublished: false,
    };
};

export const updateScheduledMatchObjectTimes = (
    match,
    start,
    end,
    courtId,
    facilityId,
    eventSchedule,
) => {
    return {
        ...match,
        startTime: moment.utc(start).tz(eventSchedule?.timeZone?.value).toISOString(),
        endTime: moment.utc(end).tz(eventSchedule?.timeZone?.value).toISOString(),
        courtId,
        facilityId,
        isPublished: false,
    };
};

export const isMatchConflictingWithOtherMatches = (newMatch, otherScheduledMatches = [], eventSchedule = {}) => {
    const conflictedMatches = otherScheduledMatches
        .filter((match) => newMatch.courtId === match.courtId)
        .filter((match) => {
            const newMatchStartTime = moment.utc(newMatch.startTime).tz(eventSchedule?.timeZone?.value);
            const matchStartTime = moment.utc(match.startTime).tz(eventSchedule?.timeZone?.value);
            const newMatchEndTime = moment.utc(newMatch.endTime).tz(eventSchedule?.timeZone?.value);
            const matchEndTime = moment.utc(match.endTime).tz(eventSchedule?.timeZone?.value);

            const isNewMatchStartTimeBetween = newMatchStartTime.isBetween(matchStartTime, matchEndTime, 'second', '[)');
            const isNewMatchEndTimeBetween = newMatchEndTime.isBetween(matchStartTime, matchEndTime, 'second', '(]');
            const isMatchStartTimeBetween = matchStartTime.isBetween(newMatchStartTime, newMatchEndTime, 'second', '[)');
            const isMatchEndTimeBetween = matchEndTime.isBetween(newMatchStartTime, newMatchEndTime, 'second', '(]');

            return isNewMatchStartTimeBetween || isNewMatchEndTimeBetween || isMatchStartTimeBetween || isMatchEndTimeBetween;
        });

    if (conflictedMatches.length > 0) {
        return {
            isConflicting: true,
            message: CONFLICT_MESSAGE_OTHER_MATCH_ON_COURT,
        };
    }

    return { isConflicting: false };
};

export const isMatchConflictingWithDayConfig = (newMatch, dayConfig) => {
    const newMatchStartTime = moment.utc(newMatch.startTime);
    const newMatchEndTime = moment.utc(newMatch.endTime);

    const dayConfigStartTime = moment.utc(dayConfig.startTime);
    const dayConfigEndTime = moment.utc(dayConfig.endTime);

    if (newMatchStartTime.isBefore(dayConfigStartTime)) {
        return {
            isConflicting: true,
            message: CONFLICT_MESSAGE_BEFORE_DAY_START,
        };
    }

    // Day-end picker caps at 23:59, so a 60-min match dropped at 23:00 would
    // otherwise fail by one minute. Allow a short grace past the configured end.
    const dayConfigEndTimeWithGrace = dayConfigEndTime.clone().add(DAY_END_GRACE_PERIOD_MINUTES, 'minutes');

    if (newMatchEndTime.isAfter(dayConfigEndTimeWithGrace)) {
        return {
            isConflicting: true,
            message: CONFLICT_MESSAGE_AFTER_DAY_END,
        };
    }

    return { isConflicting: false };
};

export const isMatchConflictingWithDrawStartDate = (newMatch, drawsList = []) => {
    const matchDraw = drawsList.find((draw) => {
        return draw.id === newMatch.drawId ||
            draw.id === newMatch.parentDrawId;
    });

    if (matchDraw?.startDateTimeUtc) {
        const newMatchStartTime = moment.utc(newMatch.startTime);
        const drawStartTime = moment.utc(matchDraw.startDateTimeUtc);

        if (drawStartTime.isAfter(newMatchStartTime)) {
            return {
                isConflicting: true,
                message: CONFLICT_MESSAGE_BEFORE_DRAW_START,
            };
        }
    }

    return { isConflicting: false };
};

export const isMatchConflictingWithOtherMatchStatus = (newMatch, otherScheduledMatches = [], eventSchedule = {}) => {
    const conflictedMatches = otherScheduledMatches
        .filter((match) => newMatch.courtId === match.courtId)
        .filter((match) => {
            const newMatchStartTime = moment.utc(newMatch.startTime).tz(eventSchedule?.timeZone?.value);
            const matchStartTime = moment.utc(match.startTime).tz(eventSchedule?.timeZone?.value);
            const isNewMatchStartTimeBefore = newMatchStartTime.isBefore(matchStartTime, matchStartTime, 'second', '()');
            return isNewMatchStartTimeBefore && hasSchedularStatus(match);
        });

    if (conflictedMatches.length > 0) {
        return {
            isConflicting: true,
            message: CONFLICT_MESSAGE_BEFORE_STARTED_MATCH,
        };
    }

    return { isConflicting: false };
};

export const checkMatchTimeConflicts = (newMatch, selectedDateCourtConfig, scheduledMatches = [], drawsList = [], eventSchedule = {}) => {
    const dayConfigConflicts = isMatchConflictingWithDayConfig(newMatch, selectedDateCourtConfig);
    if (dayConfigConflicts.isConflicting) {
        return dayConfigConflicts;
    }

    const drawStartTimeConflicts = isMatchConflictingWithDrawStartDate(newMatch, drawsList);
    if (drawStartTimeConflicts.isConflicting) {
        return drawStartTimeConflicts;
    }

    const matchTimeConflicts = isMatchConflictingWithOtherMatches(newMatch, scheduledMatches, eventSchedule);
    if (matchTimeConflicts.isConflicting) {
        return matchTimeConflicts;
    }

    const matchStatusConflicts = isMatchConflictingWithOtherMatchStatus(newMatch, scheduledMatches, eventSchedule);
    if (matchStatusConflicts.isConflicting) {
        return matchStatusConflicts;
    }

    return { isConflicting: false };
};

// When a match passes the day-end check via the grace period, its end time can
// be a few minutes past day-end. The calendar grid is single-day, so we clamp
// the saved end time to day-end to keep the card visible on reload.
export const clampMatchEndTimeToDayEnd = (match, dayConfig) => {
    if (!dayConfig?.endTime) {
        return match;
    }

    const matchEndTime = moment.utc(match.endTime);
    const dayEndTime = moment.utc(dayConfig.endTime);

    if (matchEndTime.isAfter(dayEndTime)) {
        return {
            ...match,
            endTime: dayEndTime.toISOString(),
        };
    }

    return match;
};

export const hasPositionWon = (positionString, match, playersDictByMemberId, teamsDict) => {
    if (OUTCOMES_WITH_NO_WINNER.includes(match.result?.outcome) || match.result?.isWinner === false) {
        return false;
    }

    const position = match[positionString];
    if (!position) {
        return false;
    }

    // V3 path: match has participatingMembers with memberIds on positions
    if (match.participatingMembers) {
        const { participatingMembers } = match;
        const memberIds = position.memberIds ?? (position.memberId ? [position.memberId] : []);

        const playerIds = memberIds.map((memberId) => {
            const member = participatingMembers.find((m) => m.id === memberId);
            return member?.playerId;
        }).filter(Boolean);

        const winner1 = match.result?.players?.winner1;
        const winner2 = match.result?.players?.winner2;

        return playerIds.some((pid) => pid === winner1 || pid === winner2);
    }

    // Legacy path: uses playersDictByMemberId and teamsDict
    if (position?.typeId === drawPositionTypeIds.PLAYER) {
        const playerId = playersDictByMemberId?.[position.memberId]?.playerId;
        return match.result.players.winner1 === playerId || match.result.players.winner2 === playerId;
    }

    if (position?.typeId === drawPositionTypeIds.TEAM) {
        const teamMembers = teamsDict?.[position.teamId]?.members || [];

        const winningPlayers = teamMembers.filter((memberId) => {
            const playerId = playersDictByMemberId?.[memberId]?.playerId;
            return match.result.players.winner1 === playerId || match.result.players.winner2 === playerId;
        });

        return winningPlayers.length > 0;
    }

    return false;
};

export const addOrUpdateTimePreferenceToScheduledMatch = (match, scheduledMatches = []) => {
    const matchesOnSameCourtBeforeStartTime = scheduledMatches
        .filter((sMatch) => sMatch.courtId === match.courtId)
        .filter((sMatch) => {
            return moment.utc(sMatch.startTime).isBefore(moment.utc(match.startTime));
        });

    const isPreviouslyFixedTimedMatch = !match?.notBefore && match?.includesTime;
    const isPreviouslyNotBeforeMatch = match.notBefore && match.includesTime;
    const shouldRetainIncludesTime = isPreviouslyNotBeforeMatch && !isPreviouslyFixedTimedMatch;

    const notBefore = matchesOnSameCourtBeforeStartTime.length > 0;
    const includesTime = shouldRetainIncludesTime ? match?.includesTime : matchesOnSameCourtBeforeStartTime.length === 0;

    return {
        ...match,
        includesTime,
        notBefore,
    };
};

export const getDrawData = (drawsList, drawId) => {
    const defaultColor = '#c3291c';

    const values = drawsList.reduce((acc, pDraw) => {
        if (acc) {
            return acc;
        }

        if (pDraw?.childDraws?.length > 0) {
            const childDraw = pDraw?.childDraws.find((childDraw) => childDraw.id === drawId);
            if (childDraw) {
                return {
                    color: childDraw?.drawColor,
                    parentDraw: pDraw,
                    draw: childDraw,
                };
            }
        } else if (pDraw?.id === drawId) {
            return {
                color: pDraw?.drawColor,
                parentDraw: pDraw,
                draw: pDraw,
            };
        }

        return acc;
    }, null);

    return values || { color: defaultColor };
};

export const hasUnpublishedDrawsForMatches = (scheduledMatches, drawsList) => {
    const uniqueDrawIds = new Set((scheduledMatches || []).filter(m => m.drawId).map(m => m.drawId));
    return [...uniqueDrawIds].some(drawId => {
        const { draw } = getDrawData(drawsList || [], drawId);
        if (!draw) {
            return true;
        }
        return !draw.published;
    });
};

export const getMatchTimeDisplay = (match, eventSchedule) => {
    const isFixedTimeMatch = !match.notBefore && match.includesTime;
    const isNotBefore = match.notBefore && match.includesTime;

    if (isFixedTimeMatch) {
        return `Starts ${moment.utc(match.startTime).tz(eventSchedule?.timeZone?.value).format(TIME_FORMAT_12H)}`;
    }

    if (isNotBefore) {
        return `Not before ${moment.utc(match.startTime).tz(eventSchedule?.timeZone?.value).format(TIME_FORMAT_12H)}`;
    }

    return 'Follows';
};

export const getScheduledMatchObjectFromDraftOpp = (match, drawCategoryTypesDict, drawsList = []) => {
    const {
        id,
        matchId,
        scheduleDraftId,
        drawId,
        clubLocationFacilityId,
        scoringTypeId,
        allottedMatchTime,
        ...others
    } = match;

    const { draw, parentDraw, color } = getDrawData(drawsList, drawId);

    // V3: id = matchId, scheduleDraftId = old draft id
    // V2: id = draft id, matchId = match id
    const resolvedMatchId = scheduleDraftId ? id : matchId;
    const resolvedScheduledMatchId = scheduleDraftId || id;

    return {
        id: resolvedMatchId,
        scheduledMatchId: resolvedScheduledMatchId,
        drawId,
        facilityId: clubLocationFacilityId,
        drawCategoryTypeId: draw?.categoryTypeId,
        drawEliminationTypeId: draw?.eliminationTypeId,
        drawFormatTypeId: draw?.formatTypeId,
        drawBallTypeId: draw?.ballTypeId,
        drawIsTimeBased: draw?.isTimeBased,
        drawName: getDrawName(draw, parentDraw, drawCategoryTypesDict),
        parentDrawId: parentDraw?.id,
        parentDrawName: getParentDrawName(draw, parentDraw),
        drawColor: color,
        scoringTypeId: scoringTypeId || parentDraw?.scoringTypeId,
        allottedMatchTime: allottedMatchTime || parentDraw?.allottedMatchTime,
        ...others,
    };
};

export const getMatchEventFromScheduledMatch = (match, eventSchedule) => {
    return {
        start: moment.utc(match.startTime).tz(eventSchedule?.timeZone?.value).toDate(),
        end: moment.utc(match.endTime).tz(eventSchedule?.timeZone?.value).toDate(),
        resourceId: match.courtId,
        data: match,
    };
};

export const hasSchedularStatus = (match) => {
    return Object.values(matchSchedulerStatuses).includes(match.schedulerStatus);
};

export const outcomeStringMap = {
    [OutComeTypes.COMPLETED]: 'Completed',
    [OutComeTypes.DEFAULTED]: 'Defaulted',
    [OutComeTypes.WITHDREW]: 'Withdrawn',
    [OutComeTypes.RETIRED]: 'Retired',
    [OutComeTypes.INCOMPLETE]: 'Incomplete',
    [OutComeTypes.COMPLETE]: 'Tie',
    [OutComeTypes.FORFEIT]: 'Forfeited',
    [OutComeTypes.ABANDONED]: 'Abandoned Match',
    [OutComeTypes.DOUBLEWALKOVER]: 'Double Walkover',
};

export const getMatchStatus = (match) => {
    switch (match.schedulerStatus) {
        case matchSchedulerStatuses.IN_PROGRESS:
            return 'In progress';
        case matchSchedulerStatuses.COMPLETED: {
            const outcomeString = outcomeStringMap[match.result?.outcome];
            return outcomeString || titleCase(match.result?.outcome) || 'Completed';
        }
        case matchSchedulerStatuses.PAUSED:
            return 'Paused';
        default:
            return null;
    }
};

export const canChangeMatchSchedule = (match) => {
    return !hasSchedularStatus(match);
};

export const canStartMatch = (match, scheduledMatches = []) => {
    const scheduledCourtMatches = scheduledMatches
        .filter((sMatch) => sMatch.courtId === match.courtId)
        .filter((sMatch) => {
            const sMatchStartTime = moment.utc(sMatch.startTime);
            const matchStartTime = moment.utc(match.startTime);
            return sMatchStartTime.isBefore(matchStartTime, 'second', '()');
        });

    const matchesWithStatus = scheduledCourtMatches.filter(hasSchedularStatus);

    const allPreviousOnCourtMatchesAreStarted = scheduledCourtMatches.length === matchesWithStatus.length;

    return match.isPublished &&
        !hasSchedularStatus(match) &&
        allPreviousOnCourtMatchesAreStarted;
};

export const canPauseMatch = (match) => {
    return match.isPublished && match.schedulerStatus === matchSchedulerStatuses.IN_PROGRESS;
};

export const canRestartMatch = (match) => {
    return match.isPublished && match.schedulerStatus === matchSchedulerStatuses.PAUSED;
};

export const canRemoveMatch = (match) => {
    return canChangeMatchSchedule(match);
};

export const canAdjustMatch = (match) => {
    return canChangeMatchSchedule(match);
};

const hasNoScores = (sets) => sets.every(
    (set) => set.pos1Score.toString().length === 0 && set.pos2Score.toString().length === 0,
);

const getSetWinCounts = (sets) => ({
    pos1SetsWon: sets.filter((set) => pos1Won(set)).length,
    pos2SetsWon: sets.filter((set) => pos2Won(set)).length,
});

export const resolveIsWinnerAndIsCompletedByOutcomeAndScores = (matchOutcome, sets) => {
    const isCompleted = !OUTCOMES_TO_CONSIDER_AS_NOT_COMPLETED.includes(matchOutcome);
    let isWinner = !OUTCOMES_WITH_NO_WINNER.includes(matchOutcome);

    if (OUTCOMES_WITH_WINNER_BY_SCORE.includes(matchOutcome)) {
        if (hasNoScores(sets)) {
            isWinner = false;
        }

        const { pos1SetsWon, pos2SetsWon } = getSetWinCounts(sets);
        if (pos1SetsWon === pos2SetsWon) {
            isWinner = false;
        }
    }

    return { isWinner, isCompleted };
};

const resolveWinnerAndLoserFromSetScores = (sets, team1ID, team2ID) => {
    if (hasNoScores(sets)) {
        return { winningTeam: team1ID, losingTeam: team2ID };
    }

    const { pos1SetsWon, pos2SetsWon } = getSetWinCounts(sets);
    if (pos1SetsWon === pos2SetsWon) {
        return { winningTeam: team1ID, losingTeam: team2ID };
    }

    const winningTeam = pos1SetsWon > pos2SetsWon ? team1ID : team2ID;
    const losingTeam = winningTeam === team1ID ? team2ID : team1ID;
    return { winningTeam, losingTeam };
};

export const resolveWinnerAndLoserByOutcomeAndScores = (matchOutcome, sets, winningTeamOrPlayerSelected, losingTeamOrPlayerSelected, team1ID, team2ID) => {
    if (OUTCOMES_WITH_WINNER_BY_TEAM_OR_PLAYER_SELECTOR.includes(matchOutcome)) {
        return { winningTeam: winningTeamOrPlayerSelected, losingTeam: losingTeamOrPlayerSelected };
    }

    if (OUTCOMES_TO_ALLOW_SCORING.includes(matchOutcome)) {
        return resolveWinnerAndLoserFromSetScores(sets, team1ID, team2ID);
    }

    // winner1, winner2, loser1 and loser2 should always be set irrespective of game win or lose
    return { winningTeam: team1ID, losingTeam: team2ID };
};

export const FollowsByMatchStartOptions = {
    MANUAL: 1,
    AUTO_START_AFTER_PREVIOUS_DONE: 2,
};

export const FollowsByMatchStartOptionsList = [
    {
        id: FollowsByMatchStartOptions.MANUAL,
        label: 'Use manual start button after selecting match',
    },
    {
        id: FollowsByMatchStartOptions.AUTO_START_AFTER_PREVIOUS_DONE,
        label: 'Auto start when previous match is completed',
    },
];
